import { Fragment, useMemo } from "react";
import { ArrowDown, ArrowUp, Check, X, Clock, Sparkles, UserCheck, Cpu } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichText } from "@/components/lesson/RichText";
import { TaskText } from "./TaskText";
import { GridDraw } from "./GridDraw";
import { evalKeyExpr, type ItemScore, type TaskItem } from "@shared/performanceTasks";

export interface ReviewContext {
  score?: ItemScore;
  /** Keys are present on the item (attempt submitted) and may be shown. */
  showKey: boolean;
  responses: Record<string, unknown>;
}

export interface ItemInputProps {
  item: TaskItem;
  value: unknown;
  onChange?: (v: unknown) => void;
  disabled?: boolean;
  review?: ReviewContext;
}

const LETTERS = "ABCDEFGHIJ";
const selectCls = "h-9 rounded-lg border border-input bg-background px-2 text-sm min-w-[140px] disabled:opacity-80";

function Verdict({ ok }: { ok: boolean | null | undefined }) {
  if (ok === null || ok === undefined) return null;
  return ok
    ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 text-xs font-semibold"><Check className="w-3.5 h-3.5" /> Correct</span>
    : <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-300 text-xs font-semibold"><X className="w-3.5 h-3.5" /> Not quite</span>;
}

function wordCount(s: string) { return s.trim() ? s.trim().split(/\s+/).length : 0; }
function paragraphCount(s: string) { return s.trim() ? s.trim().split(/\n\s*\n/).filter(p => p.trim()).length : 0; }

export function ItemInput({ item, value, onChange, disabled, review }: ItemInputProps) {
  const key: any = (item as any).key;
  const showKey = !!review?.showKey && !!key;
  const score = review?.score;
  const set = (v: unknown) => onChange?.(v);
  const off = disabled || !onChange;

  const body = (() => {
    switch (item.type) {
      case "single_select": {
        const chosen = typeof value === "string" ? value : "";
        return (
          <div className="space-y-2" role="radiogroup">
            {item.options.map((o, i) => {
              const isChosen = chosen === o.id;
              const isKey = showKey && key.correct === o.id;
              const cls = showKey
                ? isKey ? "border-emerald-500 bg-emerald-500/10" : isChosen ? "border-rose-500 bg-rose-500/10" : "border-border"
                : isChosen ? "border-primary bg-primary/10" : "border-border hover:border-primary/50";
              return (
                <button key={o.id} type="button" role="radio" aria-checked={isChosen} disabled={off} onClick={() => set(o.id)}
                  className={`w-full text-left rounded-xl border-2 px-3 py-2.5 flex items-start gap-3 transition-colors disabled:cursor-default ${cls}`} data-testid={`opt-${item.id}-${o.id}`}>
                  <span className={`shrink-0 w-7 h-7 rounded-full text-sm font-bold flex items-center justify-center ${isChosen || isKey ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{LETTERS[i]}</span>
                  <span className="text-sm pt-1"><RichText inline text={o.text} /></span>
                  {showKey && isKey && <Check className="w-4 h-4 text-emerald-600 ml-auto shrink-0 mt-1.5" />}
                </button>
              );
            })}
          </div>
        );
      }
      case "multi_select": {
        const chosen = Array.isArray(value) ? (value as string[]) : [];
        const toggle = (id: string) => {
          if (chosen.includes(id)) return set(chosen.filter(c => c !== id));
          if (chosen.length >= item.selectCount) return set([...chosen.slice(1), id]);
          set([...chosen, id]);
        };
        const correct: string[] = showKey ? key.correct : [];
        return (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-primary">Choose {item.selectCount}. {chosen.length}/{item.selectCount} chosen.</p>
            {item.options.map((o, i) => {
              const isChosen = chosen.includes(o.id);
              const isKey = correct.includes(o.id);
              const cls = showKey
                ? isKey ? "border-emerald-500 bg-emerald-500/10" : isChosen ? "border-rose-500 bg-rose-500/10" : "border-border"
                : isChosen ? "border-primary bg-primary/10" : "border-border hover:border-primary/50";
              return (
                <button key={o.id} type="button" role="checkbox" aria-checked={isChosen} disabled={off} onClick={() => toggle(o.id)}
                  className={`w-full text-left rounded-xl border-2 px-3 py-2.5 flex items-start gap-3 transition-colors disabled:cursor-default ${cls}`} data-testid={`opt-${item.id}-${o.id}`}>
                  <span className={`shrink-0 w-7 h-7 rounded-lg text-sm font-bold flex items-center justify-center ${isChosen || isKey ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{isChosen ? <Check className="w-4 h-4" /> : LETTERS[i]}</span>
                  <span className="text-sm pt-1"><RichText inline text={o.text} /></span>
                </button>
              );
            })}
          </div>
        );
      }
      case "table_grid": {
        const v = (value && typeof value === "object" ? value : {}) as Record<string, string>;
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-border rounded-xl overflow-hidden">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold border-b border-border" />
                  {item.columns.map(c => <th key={c.id} className="px-2 py-2 font-semibold border-b border-border text-center min-w-[84px]"><RichText inline text={c.text} /></th>)}
                </tr>
              </thead>
              <tbody>
                {item.rows.map((r, ri) => {
                  const ok = score?.detail?.[r.id];
                  return (
                    <tr key={r.id} className={ri % 2 ? "bg-muted/30" : ""}>
                      <td className="px-3 py-2 border-b border-border/60 align-top">
                        <RichText inline text={r.text} />
                        {showKey && ok === false && <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">Correct: {item.columns.find(c => c.id === key.answers[r.id])?.text}</p>}
                      </td>
                      {item.columns.map(c => {
                        const chosen = v[r.id] === c.id;
                        const isKey = showKey && key.answers[r.id] === c.id;
                        return (
                          <td key={c.id} className="text-center border-b border-border/60 align-middle">
                            <button type="button" role="radio" aria-checked={chosen} disabled={off} onClick={() => set({ ...v, [r.id]: c.id })}
                              className={`w-7 h-7 rounded-full border-2 inline-flex items-center justify-center my-1 ${chosen ? (showKey ? (isKey ? "bg-emerald-500 border-emerald-500 text-white" : "bg-rose-500 border-rose-500 text-white") : "bg-primary border-primary text-primary-foreground") : (isKey ? "border-emerald-500" : "border-border hover:border-primary")}`}
                              data-testid={`grid-${item.id}-${r.id}-${c.id}`}>
                              {chosen && <Check className="w-4 h-4" />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      case "matching": {
        const v = (value && typeof value === "object" ? value : {}) as Record<string, string>;
        return (
          <div className="space-y-2">
            {item.left.map(l => {
              const ok = score?.detail?.[l.id];
              return (
                <div key={l.id} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-border px-3 py-2">
                  <span className="text-sm flex-1"><RichText inline text={l.text} /></span>
                  <select className={selectCls} value={v[l.id] || ""} disabled={off} onChange={e => set({ ...v, [l.id]: e.target.value })} data-testid={`match-${item.id}-${l.id}`}>
                    <option value="">Choose...</option>
                    {item.right.map(r => <option key={r.id} value={r.id}>{r.text}</option>)}
                  </select>
                  {showKey && <span className="text-xs">{ok ? <Verdict ok /> : <span className="text-emerald-700 dark:text-emerald-300">Correct: {item.right.find(r => r.id === key.pairs[l.id])?.text}</span>}</span>}
                </div>
              );
            })}
          </div>
        );
      }
      case "sequence": {
        const order: string[] = Array.isArray(value) && value.length === item.items.length ? (value as string[]) : item.items.map(x => x.id);
        const move = (i: number, d: number) => { const j = i + d; if (j < 0 || j >= order.length) return; const n = [...order]; [n[i], n[j]] = [n[j], n[i]]; set(n); };
        return (
          <div className="space-y-2">
            {!off && <p className="text-xs font-semibold text-primary">Use the arrows to put these in the correct order.{Array.isArray(value) ? "" : " Nothing is saved until you move one."}</p>}
            {order.map((id, i) => {
              const it = item.items.find(x => x.id === id);
              const ok = showKey ? key.order[i] === id : undefined;
              return (
                <div key={id} className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 ${showKey ? (ok ? "border-emerald-500 bg-emerald-500/10" : "border-rose-500 bg-rose-500/10") : "border-border"}`}>
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="text-sm flex-1"><RichText inline text={it?.text || id} /></span>
                  {!off && (
                    <span className="flex gap-1">
                      <button type="button" className="p-1 rounded-md hover:bg-muted disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`seq-up-${item.id}-${id}`}><ArrowUp className="w-4 h-4" /></button>
                      <button type="button" className="p-1 rounded-md hover:bg-muted disabled:opacity-30" disabled={i === order.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`seq-down-${item.id}-${id}`}><ArrowDown className="w-4 h-4" /></button>
                    </span>
                  )}
                </div>
              );
            })}
            {showKey && score?.correct === false && (
              <p className="text-xs text-emerald-700 dark:text-emerald-300">Correct order: {key.order.map((id: string, i: number) => `${i + 1}. ${item.items.find(x => x.id === id)?.text}`).join("  ")}</p>
            )}
          </div>
        );
      }
      case "fill_blank": {
        const v = (value && typeof value === "object" ? value : {}) as Record<string, string>;
        const parts = item.text.split(/(\[\[[a-zA-Z0-9_-]+\]\])/g);
        return (
          <div className="space-y-3">
            {item.wordBank && item.wordBank.length > 0 && (
              <div className="flex flex-wrap gap-1.5 rounded-xl border border-border bg-muted/40 p-2">
                {item.wordBank.map((w, i) => <span key={i} className="rounded-full bg-card border border-border px-2.5 py-0.5 text-sm font-medium">{w}</span>)}
              </div>
            )}
            <p className="text-sm leading-9">
              {parts.map((p, i) => {
                const m = p.match(/^\[\[([a-zA-Z0-9_-]+)\]\]$/);
                if (!m) return <Fragment key={i}><RichText inline text={p} /></Fragment>;
                const b = item.blanks.find(x => x.id === m[1]);
                const ok = score?.detail?.[m[1]];
                const ring = showKey ? (ok ? "border-emerald-500" : "border-rose-500") : "border-primary/50";
                const opts = b?.options ?? (item.wordBank && item.wordBank.length ? item.wordBank : undefined);
                return (
                  <Fragment key={i}>
                    {opts ? (
                      <select className={`${selectCls} mx-1 ${ring}`} value={v[m[1]] || ""} disabled={off} onChange={e => set({ ...v, [m[1]]: e.target.value })} data-testid={`blank-${item.id}-${m[1]}`}>
                        <option value="">...</option>
                        {opts.map((o, oi) => <option key={oi} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className={`mx-1 h-9 w-32 rounded-lg border-2 bg-background px-2 text-sm ${ring}`} value={v[m[1]] || ""} disabled={off} onChange={e => set({ ...v, [m[1]]: e.target.value })} data-testid={`blank-${item.id}-${m[1]}`} />
                    )}
                    {showKey && ok === false && <span className="text-xs text-emerald-700 dark:text-emerald-300">({key.answers[m[1]]?.[0]})</span>}
                  </Fragment>
                );
              })}
            </p>
          </div>
        );
      }
      case "numeric": {
        const v = (value && typeof value === "object" ? value : {}) as { answer?: string; working?: string };
        let expected: number | null = null;
        if (showKey) expected = key.expr ? evalKeyExpr(key.expr, review!.responses) : (key.answer ?? null);
        return (
          <div className="space-y-3">
            {item.working && (
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Show how you found your answer ({item.working.points} mark{item.working.points === 1 ? "" : "s"})</label>
                <Textarea value={v.working || ""} disabled={off} rows={5} onChange={e => set({ ...v, working: e.target.value })} placeholder="Write your working here, step by step..." className="mt-1 font-mono text-sm" data-testid={`working-${item.id}`} />
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-sm font-semibold">Answer:</label>
              <Input value={v.answer || ""} disabled={off} inputMode="decimal" onChange={e => set({ ...v, answer: e.target.value })} className={`w-40 ${showKey ? (score?.correct ? "border-emerald-500" : "border-rose-500") : ""}`} data-testid={`answer-${item.id}`} />
              {item.unit && <span className="text-sm text-muted-foreground">{item.unit}</span>}
              {showKey && <Verdict ok={score?.correct} />}
              {showKey && score?.correct === false && (expected !== null || key.acceptRange) && (
                <span className="text-xs text-emerald-700 dark:text-emerald-300">Expected: {key.acceptRange ? `between ${key.acceptRange[0]} and ${key.acceptRange[1]}` : expected}{item.unit ? ` ${item.unit}` : ""}</span>
              )}
            </div>
          </div>
        );
      }
      case "short_response": {
        const v = typeof value === "string" ? value : "";
        return <Textarea value={v} disabled={off} rows={item.lines ?? 4} onChange={e => set(e.target.value)} placeholder="Write your answer in full sentences..." className="text-sm" data-testid={`text-${item.id}`} />;
      }
      case "extended_response": {
        const v = typeof value === "string" ? value : "";
        const paras = paragraphCount(v);
        return (
          <div className="space-y-2">
            {item.checklist && item.checklist.length > 0 && (
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-sm">
                <p className="font-semibold mb-1">When you write your {item.form}, remember to:</p>
                <ul className="list-disc pl-5 space-y-0.5">{item.checklist.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </div>
            )}
            <Textarea value={v} disabled={off} rows={14} onChange={e => set(e.target.value)} placeholder={`Write your ${item.form} here. Leave a blank line between paragraphs.`} className="text-sm leading-relaxed" data-testid={`text-${item.id}`} />
            <p className={`text-xs tabular-nums ${item.maxParagraphs && paras > item.maxParagraphs ? "text-amber-700 dark:text-amber-300 font-medium" : "text-muted-foreground"}`}>
              {wordCount(v)} words, {paras} paragraph{paras === 1 ? "" : "s"}
              {item.minParagraphs && item.maxParagraphs ? ` (the paper asks for ${item.minParagraphs} to ${item.maxParagraphs})` : item.minParagraphs ? ` (the paper asks for ${item.minParagraphs})` : ""}
              {item.maxParagraphs && paras > item.maxParagraphs ? " That is more than asked. Tighten it." : ""}
            </p>
          </div>
        );
      }
      case "choice_explain": {
        const v = (value && typeof value === "object" ? value : {}) as { choice?: string; explanation?: string };
        return (
          <div className="space-y-3">
            <div className="space-y-2" role="radiogroup">
              {item.options.map((o, i) => {
                const isChosen = v.choice === o.id;
                const isKey = showKey && key.correct === o.id;
                const cls = showKey && key.correct
                  ? isKey ? "border-emerald-500 bg-emerald-500/10" : isChosen ? "border-rose-500 bg-rose-500/10" : "border-border"
                  : isChosen ? "border-primary bg-primary/10" : "border-border hover:border-primary/50";
                return (
                  <button key={o.id} type="button" role="radio" aria-checked={isChosen} disabled={off} onClick={() => set({ ...v, choice: o.id })}
                    className={`w-full text-left rounded-xl border-2 px-3 py-2.5 flex items-start gap-3 transition-colors disabled:cursor-default ${cls}`} data-testid={`opt-${item.id}-${o.id}`}>
                    <span className={`shrink-0 w-7 h-7 rounded-full text-sm font-bold flex items-center justify-center ${isChosen ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{LETTERS[i]}</span>
                    <span className="text-sm pt-1"><RichText inline text={o.text} /></span>
                  </button>
                );
              })}
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Explain your choice</label>
              <Textarea value={v.explanation || ""} disabled={off} rows={6} onChange={e => set({ ...v, explanation: e.target.value })} placeholder="Give your reasons. Use numbers or evidence from the sources." className="mt-1 text-sm" data-testid={`text-${item.id}`} />
            </div>
          </div>
        );
      }
      case "grid_draw": {
        const v = (value && typeof value === "object" ? value : {}) as Record<string, number[]>;
        return <GridDraw cols={item.cols} rows={item.rows} unitLabel={item.unitLabel} shapes={item.shapes} value={v} onChange={off ? undefined : set} disabled={off} verdict={showKey ? score?.detail : undefined} />;
      }
    }
  })();

  return (
    <div className="space-y-3">
      {body}
      {review && <ReviewFooter item={item} score={score} showKey={showKey} />}
    </div>
  );
}

function ReviewFooter({ item, score, showKey }: { item: TaskItem; score?: ItemScore; showKey: boolean }) {
  const key: any = (item as any).key;
  const pending = !score || score.status === "pending";
  const by = score?.markedBy;
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2" data-testid={`review-${item.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {pending ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-800 dark:text-amber-200 px-2.5 py-0.5 text-xs font-semibold"><Clock className="w-3.5 h-3.5" /> Waiting to be marked</span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-bold tabular-nums">{score!.score} / {score!.max} marks</span>
        )}
        {!pending && by === "auto" && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Cpu className="w-3.5 h-3.5" /> Marked automatically</span>}
        {!pending && by === "ai" && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Marked by Owlbert</span>}
        {!pending && by === "admin" && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> Marked by hand</span>}
        {score?.correct !== null && score?.correct !== undefined && item.type !== "numeric" && <Verdict ok={score.correct} />}
      </div>
      {score?.criteria && score.criteria.length > 0 && (
        <ul className="space-y-1">
          {score.criteria.map(c => (
            <li key={c.id} className="text-sm flex items-start gap-2">
              <span className="w-14 shrink-0 tabular-nums font-semibold">{c.score}/{c.max}</span>
              <span><span className="font-medium">{c.name}</span>{c.note ? <span className="text-muted-foreground"> {c.note}</span> : null}</span>
            </li>
          ))}
        </ul>
      )}
      {score?.feedback && <div className="rounded-lg bg-card border border-border p-2.5 text-sm"><span className="font-semibold text-primary">Owlbert says:</span> {score.feedback}</div>}
      {showKey && key?.guidance && (item.type === "short_response" || item.type === "extended_response" || item.type === "choice_explain") && (
        <details className="text-sm"><summary className="cursor-pointer font-medium text-muted-foreground">What the markers looked for</summary><div className="mt-1 text-muted-foreground"><TaskText text={key.guidance} /></div></details>
      )}
      {showKey && item.type === "numeric" && item.working?.guidance && (
        <details className="text-sm"><summary className="cursor-pointer font-medium text-muted-foreground">A good way to work it out</summary><div className="mt-1 text-muted-foreground"><TaskText text={item.working.guidance} /></div></details>
      )}
      {showKey && item.type === "grid_draw" && key?.shapes && (
        <p className="text-xs text-muted-foreground">Expected: {item.shapes.map(s => { const k = key.shapes[s.id]; return `${s.label} ${k?.area !== undefined ? `${k.area} squares` : k?.minArea !== undefined ? `at least ${k.minArea} squares` : ""}${k?.rectangle ? " (rectangle)" : ""}`; }).join("; ")}</p>
      )}
    </div>
  );
}

export function useUnanswered(items: TaskItem[], responses: Record<string, unknown>, isAnswered: (i: TaskItem, r: unknown) => boolean) {
  return useMemo(() => items.filter(i => !isAnswered(i, responses[i.id])), [items, responses, isAnswered]);
}
